import React from "react";
import moment from "moment-timezone";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiSendVariantOutline } from "@mdi/js";
import { getEmailAlert, isProd } from "../utils/emailAutomationHelpers";
import jobBookedIcon from "../../../images/job-booked.svg";
import jobPreInspectionIcon from "../../../images/job-pre-inspection.svg";
import jobUnderInspectionIcon from "../../../images/job-under-inspection.svg";
import jobCompletedIcon from "../../../images/job-completed.svg";
import { formatAddressObject } from "../utils/eventHelpers";

const CustomEvent = ({ event, view }) => {
  const status = event?.tag || event?.status || "booked";

  // Get icon based on status
  const getStatusIcon = (status) => {
    const normalizedStatus = status?.toLowerCase();
    switch (normalizedStatus) {
      case "booked":
        return jobBookedIcon;
      case "pre-inspection":
      case "preinspection":
        return jobPreInspectionIcon;
      case "under-inspection":
      case "underinspection":
      case "under inspection":
        return jobUnderInspectionIcon;
      case "completed":
        return jobCompletedIcon;
      default:
        return jobBookedIcon;
    }
  };

  const formatTime = (startDate, endDate, isWeekView) => {
    if (isWeekView && endDate) {
      return `${moment(startDate).format("h:mm A")} - ${moment(endDate).format("h:mm A")}`;
    }
    return moment(startDate).format("h:mm A");
  };

  const getEventAddress = (event) => {
    if (event.job?.report?.address) {
      return formatAddressObject(event.job.report.address);
    }
    return "";
  };

  const getClientName = (event) => {
    return event.job?.report?.client?.name || "";
  };

  const renderContent = (time, address, clientName, isWeekView) => {
    if (isWeekView) {
      // Week view: Address (bold) + Client Name (normal) + Time (color #495057)
      if (!address && !clientName) {
        return <span style={{ color: "#495057" }}>{time}</span>;
      }
      return (
        <>
          {address && <span style={{ fontWeight: "bold" }}>{address}</span>}
          <br />
          {clientName && (
            <span style={{ fontWeight: "normal" }}>{clientName}</span>
          )}
          <br />
          <span style={{ color: "#495057" }}>{time}</span>
        </>
      );
    } else {
      // Other views: Time (bold) + Address (normal)
      if (!address) {
        return <span style={{ fontWeight: "bold" }}>{time}</span>;
      }
      return (
        <>
          <span style={{ fontWeight: "bold" }}>{time}</span>{" "}
          <span style={{ fontWeight: "normal" }}>{address}</span>
        </>
      );
    }
  };

  return (
    <div
      className="custom-event"
      style={{
        color: "#212529",
        padding: "4px 6px",
        borderRadius: "4px",
        fontSize: "11px",
        lineHeight: "1.2",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        minHeight: "28px",
        overflow: "hidden",
      }}
    >
      <img
        src={getStatusIcon(status)}
        alt={status}
        style={{
          width: "16px",
          height: "16px",
          flexShrink: 0,
          marginLeft: "-2px",
        }}
      />
      <div style={{ flex: 1, overflow: "hidden" }}>
        <div
          className="event-content"
          style={{
            fontSize: "11px",
            lineHeight: "1.2",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal",
            wordWrap: "break-word",
          }}
        >
          {renderContent(
            formatTime(event.start, event.end, view === "week"),
            getEventAddress(event),
            getClientName(event),
            view === "week"
          )}
        </div>
      </div>
      {!isProd && getEmailAlert(event.job) === "ready_to_send" && (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`email-tooltip-${event.id}`}>
              Automated email ready to send.
            </Tooltip>
          }
        >
          <span className="event-email-badge">
            <Icon path={mdiSendVariantOutline} size={0.5} color="#0D6EFD" />
          </span>
        </OverlayTrigger>
      )}
    </div>
  );
};

export default CustomEvent;
